import { compareHuge, type HugeNumber } from "@/shared/numbers";
import { NHOV_BRANCH_IDS } from "../nHov/nHovBranches";
import type { NHovBranchId } from "../types";
import type { MpmState } from "./mpmState";

export interface MpmNumericSettingChange<T = HugeNumber | number> {
  readonly section: "mhovExecution" | "nhovExecution" | "ohovExecution";
  readonly branchId?: NHovBranchId;
  readonly field:
    | "multiplier"
    | "minimumResetIntervalMs"
    | "minimumEffect"
    | "minimumTimeSeconds"
    | "minimumHovCore"
    | "minimumIntervalMs";
  readonly before: T;
  readonly after: T;
}

// Both inputs are accepted model state, never UI text or preset drafts.
export function collectMpmNumericSettingsChanges(
  before: MpmState,
  after: MpmState,
): readonly MpmNumericSettingChange[] {
  const changes: MpmNumericSettingChange[] = [];
  const observe = (change: MpmNumericSettingChange): void => {
    const equal =
      typeof change.before === "number" || typeof change.after === "number"
        ? change.before === change.after
        : compareHuge(change.before, change.after) === 0;
    if (!equal) changes.push(change);
  };
  for (const field of ["multiplier", "minimumResetIntervalMs"] as const) {
    observe({
      section: "mhovExecution",
      field,
      before: before.mhovExecution[field],
      after: after.mhovExecution[field],
    });
  }
  for (const branchId of NHOV_BRANCH_IDS) {
    for (const field of ["minimumEffect", "minimumTimeSeconds"] as const) {
      observe({
        section: "nhovExecution",
        branchId,
        field,
        before: before.nhovExecution.conditions[branchId][field],
        after: after.nhovExecution.conditions[branchId][field],
      });
    }
  }
  for (const field of ["minimumHovCore", "minimumIntervalMs"] as const) {
    observe({
      section: "ohovExecution",
      field,
      before: before.ohovExecution[field],
      after: after.ohovExecution[field],
    });
  }
  return changes;
}
